import React, { useState, useMemo } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  TableSortLabel,
  TablePagination,
  Box,
  Typography,
  Chip
} from '@mui/material';

const getActionTypeColor = (actionType) => {
  switch (actionType) {
    case 'ADD':
      return 'success';
    case 'UPDATE':
      return 'warning';
    case 'DELETE':
      return 'error';
    default:
      return 'default';
  }
};

const columns = [
  { label: 'Date & Time', key: 'created_at', sortable: true, type: 'datetime' },
  { label: 'Action', key: 'action_type', sortable: true, type: 'chip' },
  { label: 'Stock Code', key: 'stock_code', sortable: true },
  { label: 'Stock Name', key: 'stock_name', sortable: true },
  { label: 'Stock Type', key: 'stock_type', sortable: true },
  { label: 'Karatage', key: 'karatage', sortable: true },
  { label: 'Color', key: 'color', sortable: true },
  { label: 'Stone Type', key: 'stone_type', sortable: true },
  { label: 'Qty', key: 'qty', sortable: true, numeric: true },
  { label: 'Weight Gms', key: 'weight_gms', sortable: true, numeric: true },
  { label: 'Pure Weight', key: 'pure_weight', sortable: true, numeric: true },
  { label: 'Weight CTS', key: 'weight_cts', sortable: true, numeric: true },
  { label: 'Size', key: 'size', sortable: true },
  { label: 'Reference', key: 'ref_no', sortable: true },
  { label: 'Remarks', key: 'remarks', sortable: true },
  { label: 'Created By', key: 'created_by', sortable: true },
  { label: 'Batch Number', key: 'batch_number', sortable: true },
  { label: 'Transaction Ref', key: 'transaction_reference', sortable: true },
  { label: 'Previous Qty', key: 'previous_qty', sortable: true, numeric: true },
  { label: 'Previous Weight', key: 'previous_weight', sortable: true, numeric: true },
  { label: 'New Qty', key: 'new_qty', sortable: true, numeric: true },
  { label: 'New Weight', key: 'new_weight', sortable: true, numeric: true },
];

const StoneStockAddedReportTable = ({ data, loading }) => {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('date');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Handle numeric values
      if (columns.find(col => col.key === orderBy)?.numeric) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      } else {
        // Handle string values
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
      }
      
      if (order === 'desc') {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
    });
  }, [data, order, orderBy]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const createSortHandler = (property) => (event) => {
    handleRequestSort(property);
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === col.key ? order : false}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.key}
                      direction={orderBy === col.key ? order : 'asc'}
                      onClick={createSortHandler(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                    <Typography sx={{ ml: 2 }}>Loading...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography sx={{ py: 2 }}>No data found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, idx) => (
                <TableRow 
                  key={idx}
                  sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                >
                  {columns.map((col) => (
                    <TableCell 
                      key={col.key} 
                      align={col.numeric ? 'right' : 'left'}
                    >
                      {col.type === 'datetime' && row[col.key] ? 
                        new Date(row[col.key]).toLocaleString() :
                        col.type === 'chip' && row[col.key] ?
                        <Chip 
                          label={row[col.key]} 
                          color={getActionTypeColor(row[col.key])}
                          size="small"
                          variant="outlined"
                        /> :
                        col.numeric && row[col.key] ? 
                        parseFloat(row[col.key]).toFixed(3) : 
                        row[col.key] || '-'
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
      />
    </Paper>
  );
};

export default StoneStockAddedReportTable; 